import React from "react";
import { GlobalStyles } from "@mui/material";
import { Icons } from "../SkillsAcademy/icons.jsx";
import {
  AccordionItem,
  AccordionHeader,
  AccordionIconBox,
  AccordionTitleFlex,
  AccordionTitle,
  AccordionSubPills,
  AccordionBody,
  AccordionDesc,
  AccordionSessionList,
  AccordionSessionRow,
  AccordionSessionIcon,
  AccordionSessionInfo,
  AccordionSessionTitle,
  AccordionSessionSubtitle,
  AccordionSessionStatusIcon,
} from "../SkillsAcademy/style.js";
import { StyledPill } from "../SkillsAcademy/style.js";

// ── Types ─────────────────────────────────────────────────────────────────────

interface Session {
  id?: string;
  type: "skill" | "sim";
  title: string;
  freeTrial?: boolean;
}

interface Topic {
  name: string;
  desc?: string;
  sessions: Session[];
}

type TopicStatus = "completed" | "in-progress" | "up-next" | "locked";
type SessionStatus = "completed" | "next" | "available" | "locked";

interface Props {
  topic: Topic;
  status?: TopicStatus;
  isOpen?: boolean;
  onToggle?: () => void;
  isTrial?: boolean;
  topicIndex: number;
  sessionStatuses?: SessionStatus[];
  onSessionClick?: (scenarioId: string) => void;
}

// ── Status pill config ────────────────────────────────────────────────────────

const STATUS_PILL: Record<TopicStatus, { label: string; bg: string; fg: string; bd: string }> = {
  completed: {
    label: "Completed",
    bg: "rgba(23,178,106,0.12)",
    fg: "#17B26A",
    bd: "rgba(23,178,106,0.3)",
  },
  "in-progress": {
    label: "In progress",
    bg: "rgba(1,236,255,0.08)",
    fg: "#01ECFF",
    bd: "rgba(1,236,255,0.25)",
  },
  "up-next": {
    label: "Up next",
    bg: "rgba(168,126,255,0.10)",
    fg: "#A87EFF",
    bd: "rgba(168,126,255,0.28)",
  },
  locked: {
    label: "Locked",
    bg: "rgba(255,255,255,0.04)",
    fg: "#6B6A8A",
    bd: "rgba(255,255,255,0.08)",
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const SESSION_DURATION: Record<"skill" | "sim", string> = {
  skill: "12 min",
  sim: "8 min",
};

const SESSION_LABEL: Record<"skill" | "sim", string> = {
  skill: "Skill Development Session",
  sim: "Simulation Challenge",
};

// ── Component ─────────────────────────────────────────────────────────────────

const TopicSection = ({
  topic,
  status = "up-next",
  isOpen = false,
  onToggle,
  isTrial = false,
  topicIndex,
  sessionStatuses,
  onSessionClick,
}: Props) => {
  const skillCount = topic.sessions.filter((s) => s.type === "skill").length;
  const simCount = topic.sessions.filter((s) => s.type === "sim").length;

  const pill = STATUS_PILL[status];

  // Derive session status: prefer passed array, else fall back to simple trial logic
  const getSessionStatus = (session: Session, idx: number): SessionStatus => {
    if (sessionStatuses) return sessionStatuses[idx] ?? "available";
    // Fallback for trial: locked if not freeTrial in a locked topic
    if (isTrial && topicIndex > 0) return "locked";
    if (isTrial && !session.freeTrial) return "locked";
    return "available";
  };

  return (
    <>
    <GlobalStyles styles="@keyframes mp-blink { 0%,100%{opacity:0.08} 50%{opacity:1} }" />
    <AccordionItem>
      <AccordionHeader type="button" onClick={onToggle}>
        <AccordionIconBox $isOpen={isOpen}>
          <Icons.chevRight
            size={16}
            style={{
              color: isOpen ? "#01ECFF" : "#A87EFF",
              transform: isOpen ? "rotate(90deg)" : undefined,
              transition: "transform 200ms",
            }}
          />
        </AccordionIconBox>
        <AccordionTitleFlex>
          <AccordionTitle $isOpen={isOpen}>{topic.name}</AccordionTitle>
          <AccordionSubPills>
            {skillCount > 0 && (
              <StyledPill
                $bg="rgba(1,236,255,0.06)"
                $fg="#01ECFF"
                $bd="rgba(1,236,255,0.18)"
                $isSmall
              >
                <Icons.book size={10} />
                {skillCount} Skill Development Session{skillCount !== 1 ? "s" : ""}
              </StyledPill>
            )}
            {simCount > 0 && (
              <StyledPill
                $bg="rgba(150,46,255,0.06)"
                $fg="#A87EFF"
                $bd="rgba(150,46,255,0.18)"
                $isSmall
              >
                <Icons.mic size={10} />
                {simCount} Simulation Challenge{simCount !== 1 ? "s" : ""}
              </StyledPill>
            )}
          </AccordionSubPills>
        </AccordionTitleFlex>

        {/* Status pill — "In progress" gets a pulsating dot; "Locked" gets a lock icon */}
        <StyledPill $bg={pill.bg} $fg={pill.fg} $bd={pill.bd} $isSmall>
          {status === "in-progress" && (
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "#01ECFF",
                flexShrink: 0,
                animation: "mp-blink 1.6s ease-in-out infinite",
              }}
            />
          )}
          {status === "locked" && <Icons.lock size={10} />}
          {pill.label}
        </StyledPill>
      </AccordionHeader>

      {isOpen && (
        <AccordionBody>
          {topic.desc && <AccordionDesc>{topic.desc}</AccordionDesc>}
          <AccordionSessionList>
            {topic.sessions.map((session, idx) => {
              const isSim = session.type === "sim";
              const sessionStatus = getSessionStatus(session, idx);
              const isLocked = sessionStatus === "locked";
              const isCompleted = sessionStatus === "completed";
              const isNext = sessionStatus === "next";

              return (
                <AccordionSessionRow
                  key={idx}
                  $isSim={isSim}
                  $isLocked={isLocked}
                  $status={sessionStatus}
                  onClick={!isLocked && session.id && onSessionClick
                    ? () => onSessionClick(session.id!)
                    : undefined}
                  style={!isLocked && session.id && onSessionClick ? { cursor: "pointer" } : undefined}
                >
                  {/* Left icon box */}
                  <AccordionSessionIcon $isSim={isSim} $isCompleted={isCompleted}>
                    {isSim ? (
                      <Icons.mic size={14} style={{ color: isLocked ? "#6B6A8A" : isCompleted ? "#7FE5B0" : "#A87EFF" }} />
                    ) : (
                      <Icons.book size={14} style={{ color: isLocked ? "#6B6A8A" : isCompleted ? "#7FE5B0" : "#01ECFF" }} />
                    )}
                  </AccordionSessionIcon>

                  {/* Title + subtitle */}
                  <AccordionSessionInfo>
                    <AccordionSessionTitle $isCompleted={isCompleted}>{session.title}</AccordionSessionTitle>
                    <AccordionSessionSubtitle $isCompleted={isCompleted}>
                      {SESSION_LABEL[session.type]} · {SESSION_DURATION[session.type]}
                    </AccordionSessionSubtitle>
                  </AccordionSessionInfo>

                  {/* Right-side indicator */}
                  {isLocked ? (
                    <StyledPill
                      $bg="rgba(255,255,255,0.04)"
                      $fg="#6B6A8A"
                      $bd="rgba(255,255,255,0.08)"
                      $isSmall
                    >
                      <Icons.lock size={10} />
                      Locked
                    </StyledPill>
                  ) : isCompleted ? (
                    <AccordionSessionStatusIcon $status="completed">
                      <Icons.check size={12} style={{ color: "#7FE5B0" }} />
                    </AccordionSessionStatusIcon>
                  ) : isNext ? (
                    <AccordionSessionStatusIcon $status="next">
                      <Icons.play size={10} style={{ color: "#01ECFF" }} />
                    </AccordionSessionStatusIcon>
                  ) : null}
                </AccordionSessionRow>
              );
            })}
          </AccordionSessionList>
        </AccordionBody>
      )}
    </AccordionItem>
    </>
  );
};

export default TopicSection;
